import React from "react";
import {
  View,
  KeyboardAvoidingView,
  FlatList,
  Text,
  StyleSheet,
  Platform,
} from "react-native";

import moment from "moment";

import MyReportsFabButton from "./MyReportsFabButton";
import Search from "../../common/search-bar/Search";
import AppTheme from "../../AppTheme";
import appStyles from "../../../appStyles";
import Utils, {
  hasItems,
  isAndroid,
  navigate,
} from "../../../../helpers/utils";
import Header from "../../common/header/index";
import TeamIcon from "../../common/icons/TeamIcon";
import RecipientsList from "../users/RecipientsList";
import RecipientsDialog from "../users/RecipientsDialog";
import {
  getAllUsers,
  groupReportsByTeam,
  computeReportReceivers,
} from "../../users/UsersHelper";
import ReportItemWrapper from "./ReportItemWrapper";
import Toggle from "../../common/Toggle";
import appPages from "../../../../appPages";
import EmptyPageIcons from "../../common/icons/empty-pages/index";
import GenericOverlay from "../../empty-pages/GenericOverlay";
import SettingsHelper from "../../more/SettingsHelper";
import { cloneDeep } from "lodash";

export default class MyReportsPage extends React.PureComponent {
  constructor(props) {
    super(props);
    this.controller = props.controller;

    this.state = {
      allReports: [],
      type: props.type || "sent",
      filterMode: false,
    };
  }

  componentDidMount = async () => {
    if (this.controller.hasNavigation()) {
      this.focusListenerUnsub = this.props.navigation.addListener(
        "focus",
        () => {
          this.componentDidFocus();
        },
      );
    } else {
      this.componentDidFocus();
    }
  };

  componentWillUnmount = async () => {
    if (this.focusListenerUnsub) this.focusListenerUnsub();
  };

  componentDidFocus = async () => {
    // Coming from New Comments Card
    const type = this.props.route?.params?.type || this.state.type;

    if (type !== "sent") {
      const action = {
        action: "view",
        target: appPages.reportsPage,
      };
      this.controller.cardAction(action);
    }

    this.setState(
      (state) => ({ ...state, type }),
      () => {
        this.loadReports();
      },
    );
  };

  loadReports = async (selectedType) => {
    const type = selectedType || this.state.type;

    const users = getAllUsers(this.controller);

    let reports = this.sortReports(
      type === "sent"
        ? this.controller.state.sent_reports
        : type === "aggregated"
        ? this.controller.state.received_aggregated_reports
        : this.controller.state.received_reports,
    );

    // Add report users
    reports = computeReportReceivers(reports, type);

    // Group by team
    if (type === "received") {
      reports = groupReportsByTeam(reports, this.controller);
    }

    this.setState((state) => ({
      ...state,
      allReports: reports,
      reports,
      users,
      type,
      reportRecipients: [],
      showTutorialOverlay: !SettingsHelper.wasTutorialOverlaySeen(
        this.controller,
        "reports",
      ),
    }));
  };

  sortReports = (reports = []) => {
    if (reports) {
      reports.sort((a, b) => {
        const ra_date = moment(a.reference_day)
          // 2022.04.29 Fix quarter reports showing after weekly (03.31 vs 04.10)
          .add(a.time_unit === "quarter" ? -1 : 0, "week")
          .unix();
        const rb_date = moment(b.reference_day)
          .add(b.time_unit === "quarter" ? -1 : 0, "week")
          .unix();

        if (ra_date != rb_date) {
          return ra_date > rb_date ? -1 : 1;
        }

        const ua_date = moment(a.datetime_updated).unix();
        const ub_date = moment(b.datetime_updated).unix();

        if (ua_date != ub_date) {
          return ua_date > ub_date ? -1 : 1;
        }
        return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
      });
    }

    return reports;
  };

  // ------------------------------------- FILTER -------------------------------------
  isReportMatch = (r, pattern) => {
    const keys = ["name", "email_user", "description", "receiver_nickname"];

    let terms = [];
    keys.forEach((key) => {
      const value = r[key];
      if (!!value) {
        if (Array.isArray(value)) {
          terms = [...terms, ...value];
        } else {
          terms.push(value);
        }
      }
    });

    return terms.some((t) => t.match(pattern));
  };

  filterResults = ({ term }) => {
    let filteredreports = cloneDeep(this.state.allReports);

    // Multi terms search
    if (term) {
      const pattern = Utils.getSearchPattern(term);
      filteredreports = filteredreports.filter((r) => {
        let hasMatch = false;
        // Aggregated
        if (hasItems(r.reports)) {
          r.reports = r.reports.filter((rr) => this.isReportMatch(rr, pattern));
          hasMatch = hasItems(r.reports);
        } else {
          hasMatch = this.isReportMatch(r, pattern);
        }

        return hasMatch;
      });
    }

    this.setState((state) => ({
      ...state,
      isLoading: false,
      reports: [...filteredreports],
    }));
  };

  goBack = () => {
    const { filterMode } = this.state;

    if (filterMode) {
      this.setState((state) => ({ ...state, filterMode: false }));
    } else {
      this.controller.goBack();
      //navigate(this.controller, "rn:home_card");
    }
  };

  editReport = (report) => {
    const { type } = this.state;

    const entity = "report:" + report.id;
    const params = { reportId: report.id };
    this.controller.getApiData(entity, params, (data) => {
      navigate(this.controller, "rn:view_report", "changepage", {
        report: {
          ...data,
          users: report.users,
          wasReceivedReport: type === "received",
        },
      });
    });
  };

  toggleReportsType = () => {
    const type = this.state.type == "sent" ? "received" : "sent";
    this.loadReports(type);
  };

  refresh = () => {
    const { type } = this.state;

    const reportKey = type == "sent" ? "sent_reports" : "received_reports";
    this.controller.getApiData(reportKey, {}, () => this.loadReports(type));
  };

  // ------------------------------------- RECIPIENTS -------------------------------------
  filterByPerson = () => {
    this.setState((state) => ({ ...state, filterMode: true }));
  };

  onRecipientsSelected = (recipients) => {
    this.setState((state) => ({
      ...state,
      reportRecipients: recipients.map((r) => r.email),
      filterMode: false,
    }));
  };

  toggleRecipientsDialog = (users) => {
    this.setState((state) => ({ ...state, selectedRecipients: users }));
  };

  closeTutorialOverlay = () => {
    this.setState((state) => ({ ...state, showTutorialOverlay: false }));
    SettingsHelper.updateTutorialStatus(this.controller, { reports: true });
  };

  // ------------------------------------- EMPTY STATE -------------------------------------
  aboutThisScreen = () => {
    this.setState((state) => ({ ...state, showTutorialOverlay: true }));
  };

  // ------------------------------------- RENDER -------------------------------------
  render() {
    const {
      reports,
      users,
      mariMessage,
      type,
      filterMode,
      reportRecipients,
      selectedRecipients,
      showTutorialOverlay,
    } = this.state;

    let filteredReports = reports || [];
    if (hasItems(reportRecipients)) {
      filteredReports = filteredReports.filter((r) =>
        type != "sent"
          ? reportRecipients.includes(r.email_user)
          : reportRecipients.includes(r.receiver),
      );
    }

    return (
      <View
        style={[
          appStyles.container,
          {
            backgroundColor: AppTheme.containerBg,
          },
        ]}
      >
        {showTutorialOverlay ? (
          <GenericOverlay
            ImageTag={EmptyPageIcons.TeamsEmptyOverlay}
            onButtonPress={this.closeTutorialOverlay}
          />
        ) : null}

        {/* Recipients */}
        {selectedRecipients ? (
          <RecipientsDialog
            recipients={selectedRecipients}
            close={() => this.toggleRecipientsDialog(null)}
          />
        ) : null}

        {filterMode ? (
          <RecipientsList
            controller={this.controller}
            goBack={this.onRecipientsSelected}
            reportRecipients={reportRecipients}
          />
        ) : (
          <KeyboardAvoidingView
            style={[appStyles.kbView]}
            behavior="height"
            keyboardVerticalOffset={isAndroid ? 70 : 0}
          >
            <Header
              controller={this.controller}
              style={styles.header}
              iconTag={<TeamIcon onPress={this.filterByPerson} />}
              title={
                type === "sent" ? "My Submitted Reports" : "My Team’s Reports"
              }
            >
              <Toggle
                selected={type !== "sent" ? "Received" : "Submitted"}
                options={["Received", "Submitted"]}
                onToggle={this.toggleReportsType}
                style={{
                  marginHorizontal: 18,
                }}
              />
            </Header>

            {hasItems(filteredReports) ? (
              <FlatList
                contentContainerStyle={{ paddingBottom: 100 }}
                style={[
                  appStyles.scrollView,
                  { backgroundColor: "transparent" },
                ]}
                data={filteredReports}
                keyExtractor={(r) => {
                  let id = r.isTeamReport ? "" : r.id;
                  if (r.isTeamReport) {
                    r.reports.forEach((r) => (id += r.id + "-"));
                  }
                  return id;
                }}
                maxToRenderPerBatch={15}
                windowSize={15}
                renderItem={({ item }) => (
                  <ReportItemWrapper
                    report={item}
                    user={users[item.email_user]}
                    editReport={this.editReport}
                    //showReportUsers={this.toggleRecipientsDialog}
                    type={type}
                    controller={this.controller}
                  />
                )}
              />
            ) : (
              <View style={styles.noResults}>
                <Text>No reports found</Text>
              </View>
            )}

            <View style={[appStyles.footerOptions]}>
              <MyReportsFabButton
                selectedType={type}
                toggleReportsType={this.toggleReportsType}
                aboutThisScreen={this.aboutThisScreen}
                refresh={this.refresh}
                style={{ bottom: 30 }}
                mariOptions={{
                  mariMessage: mariMessage,
                }}
              />

              <Search
                showIcon={false}
                placeholder="Search Reports"
                controller={this.controller}
                filterResults={(text) => this.filterResults(text)}
                inputStyle={{ backgroundColor: "#FFF" }}
              />
            </View>
          </KeyboardAvoidingView>
        )}
      </View>
    );
  }
}

const styles = StyleSheet.create({
  header: {
    ...Platform.select({
      web: {
        minHeight: 105,
        paddingTop: 23,
      },
      ios: {
        minHeight: 120,
      },
    }),
  },
  noResults: {
    height: 40,
    marginTop: 40,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "row",
    paddingHorizontal: 10,
  },
});
